import { useIntegrationApp, useIntegrations } from '@integration-app/react';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Label } from 'src/components/Label';
import { Loading } from 'src/components/Loading';
import { SelectBox } from 'src/components/SelectBox';
import { Toggle } from 'src/components/Toggle';
import { ActionKey } from 'src/constants';
import { withIntegrationApp } from 'src/hocs/withIntegrationApp';
import { SkillCard } from './SkillCard';

const availableCalendars = [
  { value: 'calcom', disabled: true },
  { value: 'calcomv2' },
  { value: 'calendly' },
  { value: 'gohighlevel' },
  { value: 'microsoft-outlook' },
];

// Our version of TS doesn't have the supportedValuesOf method on Intl type
// eslint-disable-next-line @typescript-eslint/no-namespace
declare namespace Intl {
  type Key =
    | 'calendar'
    | 'collation'
    | 'currency'
    | 'numberingSystem'
    | 'timeZone'
    | 'unit';

  function supportedValuesOf(input: Key): string[];
}

export type Location = {
  kind: string;
  location?: string;
};

type EventType = {
  id: string;
  title: string;
  locations?: Location[];
};

type CalendarConfig = {
  integration_ids?: string[];
  event_type_id?: string;
  timezone?: string;
  required_optional_params?: string[];
  lookup_days?: number;
  location?: Location | null;
};

const LOOKUP_DAYS_OVERRIDES: Record<string, number> = {
  calendly: 7,
};

const AppointmentBookingSkillComponent = ({
  config,
  onConfigChange,
}: {
  config: CalendarConfig;
  onConfigChange?: (config: CalendarConfig) => void;
}) => {
  const integrationApp = useIntegrationApp();
  const {
    integrations,
    loading: integrationsLoading,
    refresh: refreshIntegrations,
  } = useIntegrations();
  const calendarIntegrations = integrations.filter((integration) =>
    availableCalendars.some((calendar) => calendar.value === integration.key),
  );
  const connectedCalendars = calendarIntegrations.filter(
    (integration) => !!integration.connection,
  );
  const [selectedCalendarIntegration, setSelectedCalendarIntegration] =
    useState<string>(config.integration_ids?.[0] || '');
  const [selectedEventTypeId, setSelectedEventTypeId] = useState<string>(
    config.event_type_id || '',
  );
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    config.location || null,
  );
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    config.timezone || '',
  );
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEmailRequired, setIsEmailRequired] = useState<boolean>(
    !!config.required_optional_params?.includes('email'),
  );
  const [allowTogglingEmailRequired, setAllowTogglingEmailRequired] =
    useState<boolean>(false);

  const getIntegrationDetails = async () => {
    setLoading(true);
    const connection = calendarIntegrations.find(
      (calendarIntegration) =>
        calendarIntegration.key === selectedCalendarIntegration,
    )?.connection;

    if (!connection) {
      setLoading(false);
      return;
    }

    const iaConfig = {
      baseURL: `https://api.integration.app/integrations/${selectedCalendarIntegration}/actions`,
      headers: {
        Authorization: `Bearer ${integrationApp.token}`,
      },
    };

    const response = await axios.request(iaConfig);
    const { items: actions } = response.data;

    const bookAppointmentAction = actions.find(
      (action: { id: string; key: string }) =>
        action.key === ActionKey.BOOK_APPOINTMENT,
    );
    if (bookAppointmentAction) {
      const isEmailRequiredInSchema = (
        bookAppointmentAction.inputSchema?.required || []
      ).includes('email');
      setAllowTogglingEmailRequired(!isEmailRequiredInSchema);
      if (isEmailRequiredInSchema) {
        setIsEmailRequired(true);
        onConfigChange?.({
          ...config,
          required_optional_params: ['email'],
        });
      } else {
        onConfigChange?.({
          ...config,
          required_optional_params: [],
        });
      }
    }

    const getEventTypeAction = actions.find(
      (action: { id: string; key: string }) =>
        action.key === ActionKey.GET_EVENT_TYPES,
    );
    if (!getEventTypeAction) {
      setLoading(false);
      return;
    }

    const eventTypesConfig = {
      baseURL: `https://api.integration.app/connections/${connection.id}/actions/${getEventTypeAction.id}/run`,
      headers: {
        Authorization: `Bearer ${integrationApp.token}`,
      },
      method: 'POST',
    };

    try {
      const eventTypesResponse = await axios.request(eventTypesConfig);
      setEventTypes(
        eventTypesResponse.data &&
          eventTypesResponse.data.output &&
          eventTypesResponse.data.output.event_types,
      );
      setLoading(false);
    } catch (error) {
      const connectionAccessor = integrationApp?.connection(connection.id);
      if (connectionAccessor) {
        await connectionAccessor.openReconnectUI();
      } else {
        console.error(error);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    getIntegrationDetails();
  }, [selectedCalendarIntegration, integrationsLoading]);

  return (
    <SkillCard
      description={
        <>
          Gives the agent the ability to help the user schedule an appointment
          by collecting key details like date and time.
        </>
      }
    >
      {loading || integrationsLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loading />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <Label className="text-neutral-600 font-medium" required>
                Calendar Provider
              </Label>
              <SelectBox
                options={calendarIntegrations
                  .map((integration) => ({
                    value: integration.key,
                    label: integration.name,
                  }))
                  .filter(
                    (integration) =>
                      !availableCalendars.find(
                        (calendar) => calendar.value === integration.value,
                      )?.disabled,
                  )}
                value={
                  selectedCalendarIntegration
                    ? {
                        value: selectedCalendarIntegration,
                        label: calendarIntegrations.find(
                          (integration) =>
                            integration.key === selectedCalendarIntegration,
                        )?.name,
                      }
                    : null
                }
                onChange={(value) => {
                  setSelectedEventTypeId('');
                  setSelectedLocation(null);
                  if (
                    !connectedCalendars.find(
                      (integration) => integration.key === value,
                    )
                  ) {
                    integrationApp.integration(value).open({
                      onClose: async () => {
                        const integration = await integrationApp
                          .integration(value)
                          .get();
                        if (integration.connection) {
                          await refreshIntegrations();
                          setSelectedCalendarIntegration(value);
                          onConfigChange?.({
                            timezone: config.timezone,
                            integration_ids: [value],
                            lookup_days:
                              LOOKUP_DAYS_OVERRIDES[value] || undefined,
                          });
                          return;
                        }
                        setSelectedCalendarIntegration('');
                        onConfigChange?.({
                          timezone: config.timezone,
                          integration_ids: [],
                        });
                      },
                    });
                  } else {
                    setSelectedCalendarIntegration(value);
                    onConfigChange?.({
                      timezone: config.timezone,
                      integration_ids: [value],
                      lookup_days: LOOKUP_DAYS_OVERRIDES[value] || undefined,
                    });
                  }
                }}
              />
            </div>

            <div className="space-y-3">
              <Label className="text-neutral-600 font-medium" required>
                Time zone
              </Label>
              <AutoCompleteSelectBox
                defaultValue={
                  selectedTimezone
                    ? { value: selectedTimezone, label: selectedTimezone }
                    : undefined
                }
                fetchOptions={(query: string) =>
                  Promise.resolve(
                    Intl.supportedValuesOf('timeZone')
                      .filter((tz) =>
                        tz.toLowerCase().includes(query.toLowerCase()),
                      )
                      .map((tz) => ({
                        value: tz,
                        label: tz,
                      })),
                  )
                }
                onChange={(value) => {
                  setSelectedTimezone(value);
                  onConfigChange?.({
                    ...config,
                    timezone: value,
                  });
                }}
              />
            </div>

            <div className="space-y-3">
              <Label className="text-neutral-600 font-medium" required>
                Event Type
              </Label>
              {selectedCalendarIntegration && eventTypes.length > 0 ? (
                <SelectBox
                  options={eventTypes.map((eventType) => ({
                    value: eventType.id,
                    label: eventType.title,
                  }))}
                  value={
                    selectedEventTypeId
                      ? {
                          value: selectedEventTypeId,
                          label: eventTypes.find(
                            (eventType) => eventType.id === selectedEventTypeId,
                          )?.title,
                        }
                      : null
                  }
                  onChange={(value) => {
                    setSelectedEventTypeId(value);
                    setSelectedLocation(null);
                    onConfigChange?.({
                      ...config,
                      event_type_id: value,
                      location: eventTypes[0].locations ? null : undefined,
                    });
                  }}
                />
              ) : (
                <div className="text-sm text-neutral-400 py-2">
                  Select a calendar provider first
                </div>
              )}
            </div>

            {selectedCalendarIntegration &&
              eventTypes.length > 0 &&
              eventTypes[0].locations && (
                <div className="space-y-3">
                  <Label className="text-neutral-600 font-medium" required>
                    Location
                  </Label>
                  <SelectBox
                    options={
                      eventTypes
                        .find(
                          (eventType) => eventType.id === selectedEventTypeId,
                        )
                        ?.locations?.map((location) => ({
                          value: JSON.stringify(location),
                          label: location.location
                            ? `${location.kind} - ${location.location}`
                            : location.kind,
                        })) || []
                    }
                    value={
                      selectedLocation
                        ? {
                            value: JSON.stringify(selectedLocation),
                            label: selectedLocation.location
                              ? `${selectedLocation.kind} - ${selectedLocation.location}`
                              : selectedLocation.kind,
                          }
                        : null
                    }
                    onChange={(value) => {
                      setSelectedLocation(JSON.parse(value));
                      onConfigChange?.({
                        ...config,
                        location: JSON.parse(value),
                      });
                    }}
                  />
                </div>
              )}

            <div className="space-y-3">
              <div className="flex gap-2">
                <Label
                  className="text-neutral-600 font-medium"
                  required={!allowTogglingEmailRequired}
                >
                  Collect user's email
                </Label>
                <Toggle
                  id="email-required"
                  ariaLabel="Collect user's email"
                  initialState={isEmailRequired}
                  onChange={(checked) => {
                    setIsEmailRequired(checked);
                    onConfigChange?.({
                      ...config,
                      required_optional_params: checked ? ['email'] : [],
                    });
                  }}
                  disabled={!allowTogglingEmailRequired}
                />
              </div>
              {allowTogglingEmailRequired && !isEmailRequired && (
                <div className="text-warning-500">
                  ⚠️ Ensure the selected event type supports this in your
                  provider's settings
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </SkillCard>
  );
};

export const AppointmentBookingSkill = withIntegrationApp(
  AppointmentBookingSkillComponent,
);
